import { useEffect, useRef, useState } from 'react';
import { useSeksjoner, useSaveSeksjon, useSeksjonClient } from '@origo/seksjon';
import type { Område } from '@origo/seksjon';
import type { OmrådeDragTarget } from '../components/NavPanel';
import type { ConvertedTarget } from '../components/OmrådeEditorPanel';
import type { CopySource } from '../components/KonverterTilDelområdePanel';
import { resolveTargetParentGlobalId } from '../components/veiledningKopiUtils';

interface PendingCopy {
    omrade: Område;
    target: OmrådeDragTarget;
}

export interface CopyVeiledningerResult {
    result: ConvertedTarget | null;
    error: Error | null;
}

export function useCopyVeiledninger(planId: string) {
    const [pending, setPending] = useState<PendingCopy | null>(null);
    const [copyResult, setCopyResult] = useState<CopyVeiledningerResult | null>(null);
    const hasRun = useRef(false);

    const saveMutation = useSaveSeksjon();
    const seksjonClient = useSeksjonClient();

    const kildeSections = useSeksjoner(
        pending ? { type: 'Område', planId, områdeId: pending.omrade.id } : null,
        { includeOverriddenAncestors: true }
    );

    useEffect(() => {
        if (!pending) return;
        if (hasRun.current) return;
        if (kildeSections.isLoading || !kildeSections.data) return;

        hasRun.current = true;

        const { omrade, target } = pending;

        const targetKind: 'delomrade' | 'delomradetype' =
            target.parentType === 'Delområdetype' ? 'delomradetype' : 'delomrade';

        const targetSeksjonstype =
            target.parentType === 'Delområdetype' ? 'Delområdetype' : 'Delområde';

        const targetOmrådeId = target.parentId;

        const targetDelomradetype =
            target.parentType === 'Delområdetype' ? target.parentId : target.delomradetype;

        const kilde = kildeSections.data.filter(
            (s) =>
                !s.isInherited &&
                s.seksjonstype === 'Område' &&
                s.områdeId === omrade.id
        );

        const elementRepo = seksjonClient.elementer;

        (async () => {
            const addFailures: { tittel: string; error: string }[] = [];
            const copiedGlobalIds: string[] = [];
            const copiedObjectIds: number[] = [];
            let successCount = 0;

            for (const s of kilde) {
                try {
                    const newParentGlobalId = s.overstyrer
                        ? resolveTargetParentGlobalId(
                            s.parentGlobalId,
                            kildeSections.data ?? [],
                            targetKind
                        )
                        : undefined;
                    const preserveOverride = s.overstyrer && !!newParentGlobalId;

                    const newSeksjon = await saveMutation.mutateAsync({
                        values: {
                            seksjonstype: targetSeksjonstype,
                            planId,
                            områdeId: targetOmrådeId,
                            delområdetype: targetDelomradetype,
                            områdeNavn: target.navn,
                            tittel: s.tittel,
                            tekst: s.tekst,
                            synlig: s.synlig,
                            kommunal: s.kommunal,
                            infoboks: s.infoboks,
                            topp: s.topp,
                            kunUregulert: s.kunUregulert,
                            prioritet: s.prioritet,
                            overstyrer: preserveOverride,
                            parentGlobalId: preserveOverride ? newParentGlobalId : undefined,
                        },
                    });

                    if (elementRepo && s.globalId && newSeksjon.globalId) {
                        const sourceElements = await elementRepo.fetchForSection(s.globalId);
                        for (const el of sourceElements) {
                            await elementRepo.add({
                                seksjonsGlobalId: newSeksjon.globalId,
                                filId: el.filId,
                                galleriElementId: el.galleriElementId,
                                tittel: el.tittel,
                                beskrivelse: el.beskrivelse,
                                prioritet: el.prioritet,
                            });
                        }
                    }

                    if (newSeksjon.globalId) copiedGlobalIds.push(newSeksjon.globalId);
                    if (newSeksjon.objectId != null) copiedObjectIds.push(newSeksjon.objectId);
                    successCount++;
                } catch (saveErr) {
                    const msg = (saveErr as Error)?.message ?? String(saveErr);
                    addFailures.push({ tittel: s.tittel, error: msg });
                    console.error('[DragCopy] kopiering feilet for', s.tittel, saveErr);
                }
            }

            if (addFailures.length > 0) {
                setCopyResult({
                    result: null,
                    error: new Error(
                        `${addFailures.length} av ${kilde.length} veiledningspunkt kunne ikke kopieres: ${addFailures
                            .map((f) => `"${f.tittel}" (${f.error})`)
                            .join(', ')}`
                    ),
                });
                setPending(null);
                return;
            }

            const copiedFrom: CopySource = {
                id: omrade.id,
                navn: omrade.navn,
                sectionCount: successCount,
                copiedGlobalIds,
                copiedObjectIds,
            };

            const result: ConvertedTarget =
                target.parentType === 'Delområdetype'
                    ? { kind: 'delomradetype', id: target.parentId, navn: target.navn, copiedFrom }
                    : { kind: 'delomrade', id: target.parentId, navn: target.navn, delomradetype: target.delomradetype, copiedFrom };

            setCopyResult({ result, error: null });
            setPending(null);
        })().catch((err: Error) => {
            setCopyResult({ result: null, error: err });
            setPending(null);
        });
    }, [pending, kildeSections.isLoading, kildeSections.data]); // eslint-disable-line react-hooks/exhaustive-deps

    function execute(omrade: Område, target: OmrådeDragTarget) {
        hasRun.current = false;
        setCopyResult(null);
        setPending({ omrade, target });
    }

    function clearResult() {
        setCopyResult(null);
    }

    return { execute, copyResult, clearResult, isPending: !!pending };
}
